import { useState, useRef, useEffect } from "react";
import { AutoComplete } from "primereact/autocomplete";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Toolbar } from "primereact/toolbar";
import { Avatar } from "primereact/avatar";
import { addContact, getContacts, getUsersContact, updateContact } from "./Integracion";

const EMPTY_FORM = { name: "", phone: "", email: "", role: "", status: "activo" };
const statusSeverity = { activo: "success", inactivo: "danger" };

export default function CatalogoSumaDrivers() {
    const [contacts, setContacts] = useState([]);
    const [userContacts, setUserContacts] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [dialogVisible, setDialogVisible] = useState(false);
    const [statusDialog, setStatusDialog] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [contactToUpdate, setContactToUpdate] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const toast = useRef(null);

    useEffect(() => {
        getContacts().then(setContacts).catch(() => {
            toast.current.show({
                severity: "error",
                summary: "Error al obtener los datos",
                detail: "No se pueden obtener los contactos, inténtalo más tarde",
                life: 3000
            });
        });
        getUsersContact().then(setUserContacts);
    }, []);

    const searchUser = (e) => {
        const q = e.query.toLowerCase();
        setSuggestions(
            userContacts.filter(u =>
                u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
            )
        );
    };

    const onUserSelect = (e) => {
        const u = e.value;
        setSelectedUser(u);
        setForm({ name: u.name, phone: u.phone ?? "", email: u.email, role: u.role ?? "", status: "activo" });
    };

    const openNew = () => {
        setForm(EMPTY_FORM);
        setSelectedUser(null);
        setSubmitted(false);
        setDialogVisible(true);
    };

    const saveContact = () => {
        setSubmitted(true);
        if (!selectedUser?.id) return;
        addContact(selectedUser.id).then(() => {
            setContacts([...contacts, { ...form, id: selectedUser.id }]);
            toast.current.show({ severity: "success", summary: "Agregado", detail: "Contacto agregado correctamente", life: 3000 });
            setDialogVisible(false);
        }).catch(err => {
            toast.current.show({ severity: "error", summary: "Error al agregar", detail: err.message, life: 3000 });
        });
    };

    const openStatusDialog = (contact) => {
        setContactToUpdate(contact);
        setStatusDialog(true);
    };

    const changeStatus = (newStatus) => {
        const flag = newStatus === "activo" ? 1 : 0;
        updateContact(contactToUpdate.id, flag).then(() => {
            setContacts(contacts.map(c =>
                c.id === contactToUpdate.id ? { ...c, status: newStatus } : c
            ));
            setStatusDialog(false);
            toast.current.show({
                severity: newStatus === "activo" ? "success" : "warn",
                summary: newStatus === "activo" ? "Activado" : "Desactivado",
                detail: `${contactToUpdate.name} fue ${newStatus === "activo" ? "activado" : "desactivado"}`,
                life: 3000
            });
        }).catch(err => {
            toast.current.show({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
        });
    };

    // Templates
    const avatarTemplate = (row) => (
        <div className="flex align-items-center gap-2">
            <Avatar label={row.name.charAt(0)} shape="circle" style={{ backgroundColor: "#3B82F6", color: "#fff", flexShrink: 0 }} />
            <span className="font-medium">{row.name}</span>
        </div>
    );

    const statusTemplate = (row) => (
        <Tag value={row.status === "activo" ? "Activo" : "Inactivo"} severity={statusSeverity[row.status]} />
    );

    const actionsTemplate = (row) => (
        row.status === "activo"
            ? <Button icon="pi pi-user-minus" rounded text severity="danger"
                      onClick={() => openStatusDialog(row)}
                      tooltip="Desactivar" tooltipOptions={{ position: "top" }} />
            : <Button icon="pi pi-user-plus" rounded text severity="success"
                      onClick={() => openStatusDialog(row)}
                      tooltip="Activar" tooltipOptions={{ position: "top" }} />
    );

    const isActivo = contactToUpdate?.status === "activo";

    const statusFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={() => setStatusDialog(false)} />
            {isActivo
                ? <Button label="Desactivar" icon="pi pi-user-minus" severity="danger" onClick={() => changeStatus("inactivo")} />
                : <Button label="Activar" icon="pi pi-user-plus" severity="success" onClick={() => changeStatus("activo")} />
            }
        </div>
    );

    return (
        <div className="p-3" style={{ backgroundColor: "#F8FAFC" }}>
            <Toast ref={toast} />

            <Toolbar
                start={
                    <div className="flex align-items-center gap-3">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-car text-blue-500" style={{ fontSize: "1.4rem" }} />
                            <span className="font-bold text-xl" style={{ color: "#1E3A5F" }}>Suma Drivers</span>
                        </div>
                        <span className="text-color-secondary">/ Contactos</span>
                    </div>
                }
                end={<Button label="Nuevo contacto" icon="pi pi-plus" onClick={openNew} />}
                className="mb-3"
                style={{ borderRadius: "12px" }}
            />

            <div className="card shadow-1" style={{ borderRadius: "12px", overflow: "hidden" }}>
                <DataTable
                    value={contacts}
                    globalFilter={globalFilter}
                    header={
                        <div className="flex justify-content-end">
                            <IconField iconPosition="left">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    placeholder="Buscar contacto…"
                                    value={globalFilter}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                />
                            </IconField>
                        </div>
                    }
                    paginator
                    rows={15}
                    rowsPerPageOptions={[15, 30, 50]}
                    emptyMessage="Sin contactos registrados"
                    stripedRows
                    size="normal"
                >
                    <Column header="Nombre" body={avatarTemplate} sortField="name" sortable style={{ minWidth: "14rem" }} />
                    <Column field="phone" header="Teléfono" style={{ minWidth: "10rem" }} />
                    <Column field="email" header="Correo" style={{ minWidth: "14rem" }} />
                    <Column field="role" header="Rol" sortable style={{ minWidth: "10rem" }} />
                    <Column header="Estado" body={statusTemplate} sortField="status" sortable style={{ minWidth: "8rem" }} />
                    <Column header="Acciones" body={actionsTemplate} style={{ minWidth: "6rem" }} />
                </DataTable>
            </div>

            {/* Nuevo contacto */}
            <Dialog
                visible={dialogVisible}
                header="Nuevo contacto"
                style={{ width: "min(95vw, 480px)" }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" icon="pi pi-times" outlined onClick={() => setDialogVisible(false)} />
                        <Button label="Agregar" icon="pi pi-check" onClick={saveContact} />
                    </div>
                }
                onHide={() => setDialogVisible(false)}
                draggable={false}
            >
                <div className="formgrid grid pt-2">
                    <div className="field col-12">
                        <label className="font-medium mb-1 block">Buscar usuario del sistema</label>
                        <AutoComplete
                            value={selectedUser}
                            suggestions={suggestions}
                            completeMethod={searchUser}
                            field="name"
                            onSelect={onUserSelect}
                            onChange={e => setSelectedUser(e.value)}
                            placeholder="Escribe nombre o correo…"
                            className="w-full"
                            inputClassName="w-full"
                            itemTemplate={u => (
                                <div className="py-1">
                                    <div className="font-medium">{u.name}</div>
                                    <div className="text-sm text-color-secondary">{u.email} · {u.role}</div>
                                </div>
                            )}
                        />
                        {submitted && !selectedUser?.id && (
                            <small className="p-error">Selecciona un usuario del sistema</small>
                        )}
                    </div>

                    {selectedUser && [
                        { label: "Nombre", icon: "pi-user", key: "name" },
                        { label: "Teléfono", icon: "pi-phone", key: "phone" },
                        { label: "Correo electrónico", icon: "pi-envelope", key: "email" },
                        { label: "Rol", icon: "pi-briefcase", key: "role" },
                    ].map(({ label, icon, key }) => (
                        <div className="field col-12" key={key}>
                            <label className="font-medium mb-1 block">{label}</label>
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className={`pi ${icon}`} />
                                <InputText
                                    value={form[key]}
                                    readOnly
                                    className="w-full"
                                    style={{ backgroundColor: "#f1f5f9", color: "#64748b" }}
                                />
                            </IconField>
                        </div>
                    ))}
                </div>
            </Dialog>

            {/* Cambio de estado */}
            <Dialog
                visible={statusDialog}
                header={isActivo ? "Desactivar contacto" : "Activar contacto"}
                style={{ width: "min(90vw, 380px)" }}
                footer={statusFooter}
                onHide={() => setStatusDialog(false)}
                draggable={false}
            >
                <div className="flex align-items-center gap-3">
                    <i className={`pi ${isActivo ? "pi-user-minus text-red-500" : "pi-user-plus text-green-500"}`}
                       style={{ fontSize: "2rem" }} />
                    <span>
                        {isActivo
                            ? <>¿Deseas desactivar a <strong>{contactToUpdate?.name}</strong>? Su acceso quedará inhabilitado.</>
                            : <>¿Deseas activar a <strong>{contactToUpdate?.name}</strong>? Recuperará acceso al sistema.</>
                        }
                    </span>
                </div>
            </Dialog>
        </div>
    );
}
